import type { RevenueCatInitStatus } from '@/src/hooks/revenuecat-init-controller'
import type { ReactNode } from 'react'
import type { PurchasesPackage } from 'react-native-purchases'
import { Component, Suspense, useEffect, useRef } from 'react'
import { Platform, Pressable, Text, View } from 'react-native'
import { Spinner } from '@/src/components/ui/Spinner'
import { colors } from '@/src/theme'
import { body2, label1, label2 } from '@/src/theme/typography'
import { captureMessage } from '@/src/utils/sentry'
import { QueryErrorResetBoundary } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { usePurchaseOfferings } from '../hooks/usePurchaseOfferings'
import { resolvePurchaseOptionsViewState } from '../utils/purchaseScreenViewState'
import { PurchasePlanOption } from './PurchasePlanOption'

class PurchaseOptionsErrorBoundary extends Component<
  {
    children: ReactNode
    fallback: (props: { error: Error; reset: () => void }) => ReactNode
    onReset: () => void
    onError?: (error: Error) => void
  },
  { error: Error | null }
> {
  state: { error: Error | null } = {
    error: null,
  }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error) {
    this.props.onError?.(error)
  }

  reset = () => {
    this.props.onReset()
    this.setState({ error: null })
  }

  render() {
    if (this.state.error != null) {
      return this.props.fallback({
        error: this.state.error,
        reset: this.reset,
      })
    }

    return this.props.children
  }
}

function PurchaseOptionsErrorFallback({
  errorLabel,
  isCompactLayout = false,
  retryLabel,
  reset,
}: {
  errorLabel: string
  isCompactLayout?: boolean
  retryLabel: string
  reset: () => void
}) {
  return (
    <View className={isCompactLayout ? 'mb-4' : 'mb-6'}>
      <Text className="mb-3" style={[body2, { color: colors.inkSecondary }]}>
        {errorLabel}
      </Text>
      <Pressable
        className="self-start h-10 px-4 rounded-lg items-center justify-center bg-primary"
        onPress={reset}
      >
        <Text style={[label2, { color: colors.primaryForeground }]}>
          {retryLabel}
        </Text>
      </Pressable>
    </View>
  )
}

function ReadyPurchaseOptionsList({
  revenueCatUserId,
  revenueCatInitStatus,
  language,
  isCompactLayout,
  paymentChoosePlanText,
  formatCurrency,
  yearlySavingsText,
  onOptionsChange,
}: {
  revenueCatUserId: string | null
  revenueCatInitStatus: RevenueCatInitStatus
  language: string
  isCompactLayout: boolean
  paymentChoosePlanText: string
  formatCurrency: (value: number, currencyCode: string) => string
  yearlySavingsText: string | null
  onOptionsChange: (state: {
    packages: PurchasesPackage[]
    selectedPackage: PurchasesPackage | null
  }) => void
}) {
  const { t } = useTranslation('purchase')
  const onOptionsChangeRef = useRef(onOptionsChange)

  useEffect(() => {
    if (revenueCatInitStatus === 'ready') {
      return
    }

    captureMessage('offerings_requested_before_ready', 'warning', {
      feature: 'purchase',
      tags: { flow: 'fetch_offerings', platform: Platform.OS },
      extra: { revenueCatInitStatus },
    })
  }, [revenueCatInitStatus])

  useEffect(() => {
    onOptionsChangeRef.current = onOptionsChange
  }, [onOptionsChange])

  const {
    packages,
    status,
    selectedPackage,
    setSelectedPackage,
    refetchPackages,
    isRefetchingPackages,
  } = usePurchaseOfferings({
    revenueCatUserId,
    language,
  })
  const optionsViewState = resolvePurchaseOptionsViewState({
    isSessionLoading: false,
    revenueCatInitStatus,
    offeringsStatus: status,
  })

  useEffect(() => {
    onOptionsChangeRef.current({
      packages,
      selectedPackage,
    })
  }, [packages, selectedPackage])

  return (
    <>
      <Text
        className={isCompactLayout ? 'mb-3' : 'mb-4'}
        style={[label1, { color: colors.ink }]}
      >
        {paymentChoosePlanText}
      </Text>

      <View className={isCompactLayout ? 'mb-3' : 'mb-6'}>
        {packages.map((pkg) => (
          <PurchasePlanOption
            key={pkg.identifier}
            pkg={pkg}
            isCompactLayout={isCompactLayout}
            isSelected={selectedPackage?.identifier === pkg.identifier}
            onSelect={setSelectedPackage}
            formatCurrency={formatCurrency}
            monthlyLabel={t('monthly_label')}
            yearlyLabel={t('yearly_label')}
            periodMonthLabel={t('period_month')}
            periodYearLabel={t('period_year')}
            yearlySavingsText={yearlySavingsText}
          />
        ))}
        {optionsViewState === 'empty' && (
          <View className="items-start gap-3">
            <Text className="text-gray-500 italic">{t('no_offers')}</Text>
            <Pressable
              className={`h-10 px-4 rounded-lg items-center justify-center ${isRefetchingPackages ? 'bg-gray-300' : 'bg-primary'}`}
              onPress={() => void refetchPackages()}
              disabled={isRefetchingPackages}
            >
              <Text style={[label2, { color: colors.primaryForeground }]}>
                {isRefetchingPackages ? t('loading') : t('try_again')}
              </Text>
            </Pressable>
          </View>
        )}
      </View>
    </>
  )
}

export function PurchaseOptionsSection({
  isCompactLayout = false,
  isSessionLoading,
  revenueCatInitStatus,
  revenueCatUserId,
  language,
  paymentChoosePlanText,
  formatCurrency,
  yearlySavingsText,
  onRetryRevenueCatInitialization,
  onOptionsChange,
}: {
  isCompactLayout?: boolean
  isSessionLoading: boolean
  revenueCatInitStatus: RevenueCatInitStatus
  revenueCatUserId: string | null
  language: string
  paymentChoosePlanText: string
  formatCurrency: (value: number, currencyCode: string) => string
  yearlySavingsText: string | null
  onRetryRevenueCatInitialization: () => Promise<void>
  onOptionsChange: (state: {
    packages: PurchasesPackage[]
    selectedPackage: PurchasesPackage | null
  }) => void
}) {
  const { t } = useTranslation(['purchase', 'setup'])
  const onOptionsChangeRef = useRef(onOptionsChange)
  const optionsViewState = resolvePurchaseOptionsViewState({
    isSessionLoading,
    revenueCatInitStatus,
  })

  useEffect(() => {
    if (optionsViewState === 'ready') {
      return
    }

    onOptionsChangeRef.current({
      packages: [],
      selectedPackage: null,
    })
  }, [optionsViewState])

  if (optionsViewState === 'loading') {
    return (
      <View className={isCompactLayout ? 'mb-3' : 'mb-6'}>
        <Text
          className={isCompactLayout ? 'mb-3' : 'mb-4'}
          style={[label1, { color: colors.ink }]}
        >
          {paymentChoosePlanText}
        </Text>
        <Spinner size="small" />
      </View>
    )
  }

  if (optionsViewState === 'init_failed') {
    return (
      <View className={isCompactLayout ? 'mb-4' : 'mb-6'}>
        <Text
          className={isCompactLayout ? 'mb-3' : 'mb-4'}
          style={[label1, { color: colors.ink }]}
        >
          {paymentChoosePlanText}
        </Text>
        <View className="items-start gap-3">
          <Text className="text-gray-500 italic">
            {t('purchase:purchase_options_unavailable')}
          </Text>
          <Pressable
            className="h-10 px-4 rounded-lg items-center justify-center bg-primary"
            onPress={() => void onRetryRevenueCatInitialization()}
          >
            <Text style={[label2, { color: colors.primaryForeground }]}>
              {t('setup:try_again')}
            </Text>
          </Pressable>
        </View>
      </View>
    )
  }

  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <PurchaseOptionsErrorBoundary
          onReset={reset}
          onError={() => {
            onOptionsChange({
              packages: [],
              selectedPackage: null,
            })
          }}
          fallback={({ reset: resetBoundary }) => (
            <PurchaseOptionsErrorFallback
              errorLabel={t('purchase:purchase_options_unavailable')}
              isCompactLayout={isCompactLayout}
              retryLabel={t('setup:try_again')}
              reset={resetBoundary}
            />
          )}
        >
          <Suspense
            fallback={
              <View className={isCompactLayout ? 'mb-4' : 'mb-6'}>
                <Text
                  className={isCompactLayout ? 'mb-3' : 'mb-4'}
                  style={[label1, { color: colors.ink }]}
                >
                  {paymentChoosePlanText}
                </Text>
                <Spinner size="small" />
              </View>
            }
          >
            <ReadyPurchaseOptionsList
              isCompactLayout={isCompactLayout}
              revenueCatInitStatus={revenueCatInitStatus}
              revenueCatUserId={revenueCatUserId}
              language={language}
              paymentChoosePlanText={paymentChoosePlanText}
              formatCurrency={formatCurrency}
              yearlySavingsText={yearlySavingsText}
              onOptionsChange={onOptionsChange}
            />
          </Suspense>
        </PurchaseOptionsErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  )
}
